import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { PaymentMethod } from "@satyrsmc/shared/client";
import type { MemberEventDetail } from "@satyrsmc/shared/dto/member/event";
import { calculateStripeFee } from "@satyrsmc/shared/lib/stripe-fees";
import { resolveEventCost } from "@satyrsmc/shared/lib/event-pricing";
import { stripePromise } from "@/env";
import { useCreatePaymentIntent } from "@/queries/member-events";
import { useWizard } from "./WizardContext";
import { useAuth } from "@/state/AuthContext";

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  [PaymentMethod.Check]: "Check",
  [PaymentMethod.Cash]: "Cash",
  [PaymentMethod.Zelle]: "Zelle",
  [PaymentMethod.CreditCard]: "Credit Card",
};

const SELECTABLE_PAYMENT_METHODS = Object.values(PaymentMethod).filter(
  (m) => m !== PaymentMethod.Cash,
);

function StripeFields({ onReady }: { onReady: (fn: () => Promise<string>) => void }) {
  const stripe = useStripe();
  const elements = useElements();

  return (
    <PaymentElement
      onReady={() => {
        if (!stripe || !elements) return;
        onReady(async () => {
          const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            redirect: "if_required",
          });
          if (error) throw new Error(error.message ?? "Payment failed. Please try again.");
          if (
            paymentIntent?.status !== "succeeded" &&
            paymentIntent?.status !== "requires_capture"
          ) {
            throw new Error("Payment could not be confirmed. Please try again.");
          }
          return paymentIntent.id;
        });
      }}
    />
  );
}

interface Props {
  event: MemberEventDetail;
}

export function Step3Payment({ event }: Props) {
  const { state, dispatch, stripeConfirmRef } = useWizard();
  const { isMember } = useAuth();
  const isCreditCard = state.paymentMethod === PaymentMethod.CreditCard;

  // `isPending` does not flip synchronously, so it cannot keep the effect below
  // from firing twice before the first request registers — and each extra fire
  // creates a real, abandoned PaymentIntent. This ref does close that window.
  const quoteInFlightRef = useRef(false);

  const createPaymentIntent = useCreatePaymentIntent({
    onSuccess: (data) => {
      quoteInFlightRef.current = false;
      dispatch({ type: "SET_STRIPE_CLIENT_SECRET", secret: data.clientSecret });
    },
    onError: (err) => {
      quoteInFlightRef.current = false;
      toast.error(err.message || "Failed to initialize payment. Please try again.");
    },
  });

  const handleSelectPaymentMethod = (method: PaymentMethod) => {
    stripeConfirmRef.current = null;
    dispatch({ type: "SET_PAYMENT_METHOD", method });
  };

  // The day drives the price, so the intent has to be quoted for the same day the
  // RSVP will record — otherwise the card is charged the full-event rate.
  const attendanceDate = state.attendance.kind === "single" ? state.attendance.date : null;

  // Trigger payment intent creation when CC is selected and no secret yet
  useEffect(() => {
    if (isCreditCard && !state.stripeClientSecret && !quoteInFlightRef.current) {
      quoteInFlightRef.current = true;
      createPaymentIntent.mutate({ eventId: event.id, attendanceDate });
    }
  }, [isCreditCard, state.stripeClientSecret, event.id, attendanceDate, createPaymentIntent]);

  const effectiveCost = resolveEventCost(event, isMember, attendanceDate !== null);
  const baseCents = Math.round((effectiveCost ?? 0) * 100);
  const feeSummary = isCreditCard && baseCents > 0 ? calculateStripeFee(baseCents) : null;
  // A day pass is a flat rate, so there is no members' price to strike through.
  const hasMemberDiscount =
    attendanceDate === null &&
    isMember &&
    event.member_cost != null &&
    event.ga_ticket_cost != null &&
    event.member_cost !== event.ga_ticket_cost;

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium">
        {hasMemberDiscount ? (
          <span className="flex items-baseline gap-2">
            <span className="line-through text-muted-foreground">
              ${event.ga_ticket_cost!.toLocaleString()}
            </span>
            <span>${event.member_cost!.toLocaleString()}</span>
            <span className="text-xs font-normal text-muted-foreground">Members price</span>
          </span>
        ) : (
          <span>${effectiveCost?.toLocaleString()}</span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">Select your payment method.</p>

      <div className="grid grid-cols-2 gap-2">
        {SELECTABLE_PAYMENT_METHODS.map((method) => (
          <button
            key={method}
            type="button"
            className={`rounded-lg border p-3 text-sm transition-colors ${
              state.paymentMethod === method
                ? "border-primary bg-primary/10 font-medium"
                : "border-border hover:border-primary/50"
            }`}
            onClick={() => handleSelectPaymentMethod(method)}
          >
            {PAYMENT_METHOD_LABELS[method]}
          </button>
        ))}
      </div>

      {/* Fee breakdown for CC */}
      {isCreditCard && feeSummary && (
        <div className="rounded border border-border bg-muted/50 p-3 text-xs space-y-1">
          <div className="flex justify-between text-muted-foreground">
            <span>Registration fee</span>
            <span>${((feeSummary.totalCents - feeSummary.feeCents) / 100).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>
              Convenience fee <span className="italic">(covers credit card processing)</span>
            </span>
            <span>${(feeSummary.feeCents / 100).toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-medium pt-1 border-t border-border">
            <span>Total</span>
            <span>${(feeSummary.totalCents / 100).toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* Stripe payment element */}
      {isCreditCard && (
        <div>
          {createPaymentIntent.isPending && (
            <p className="text-xs text-muted-foreground">Loading payment form…</p>
          )}
          {state.stripeClientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret: state.stripeClientSecret }}>
              <StripeFields onReady={(fn) => (stripeConfirmRef.current = fn)} />
            </Elements>
          )}
          {!stripePromise && (
            <p className="text-xs text-destructive">
              Stripe is not configured. Please choose a different payment method.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
